import {createAsyncThunk} from "@reduxjs/toolkit";
import {handlerError} from "../../../store/thunkHandlers";
import {setModalMessage} from "../../messages/model/slice";
import {fetchGetInvoicesStatistics} from "../../invoices/model/actions";
import {INewTechnicalLiterature, ITechnicalLiterature} from "../../../models/ITechnicalLiterature";
import {technicalLiteratureAPI} from "../api";
import {uploadFileToS3} from "../../files/api";

export interface IAddLiteratureData {
    literature: INewTechnicalLiterature;
    file?: File | null;
}

export const fetchAddTechnicalLiterature = createAsyncThunk(
    "technical_literature/add",
    async (data: IAddLiteratureData, {dispatch, rejectWithValue}) => {
        try {
            const {literature, file} = data;
            if (file) {
                // тип файла; для произвольных файлов fallback на octet-stream
                const contentType = file.type || "application/octet-stream";

                // 1. получаем presigned URL + ключ
                const {upload_url, s3_key} = await technicalLiteratureAPI.getUploadUrl(
                    file.name,
                    contentType,
                );

                // 2. грузим файл напрямую в S3 (тот же contentType, что подписали)
                await uploadFileToS3(upload_url, file, contentType);

                // 3. сохраняем ключ в записи литературы
                literature.s3_key = s3_key;
            }

            // 4. создаём запись литературы с s3_key
            const res = await technicalLiteratureAPI.add(literature);
            dispatch(fetchGetInvoicesStatistics());
            return res;
        } catch (e) {
            const msg = handlerError(e);
            dispatch(setModalMessage(msg));
            return rejectWithValue(msg);
        }
    },
);

export const fetchGetAllTechnicalLiterature = createAsyncThunk(
    "technical_literature/get_all",
    async (_, {dispatch, rejectWithValue}) => {
        try {
            return await technicalLiteratureAPI.getAll();
        } catch (e) {
            const msg = handlerError(e);
            dispatch(setModalMessage(msg));
            return rejectWithValue(msg);
        }
    },
);

export const fetchUpdateLiterature = createAsyncThunk(
    "technical_literature/update",
    async (literature: ITechnicalLiterature, {rejectWithValue, dispatch}) => {
        try {
            return await technicalLiteratureAPI.update(literature);
        } catch (e) {
            const msg = handlerError(e);
            dispatch(setModalMessage(msg));
            return rejectWithValue(msg);
        }
    },
);

export const fetchDeleteTechnicalLiterature = createAsyncThunk(
    "technical_literature/delete",
    async (id: string, {rejectWithValue, dispatch}) => {
        try {
            // удаление файла из S3 теперь делает бэкенд внутри delete(id).
            // Фронту больше не нужно отдельно дергать filesAPI.delete.
            await technicalLiteratureAPI.delete(id);
            return id;
        } catch (e) {
            const msg = handlerError(e);
            dispatch(setModalMessage(msg));
            return rejectWithValue(msg);
        }
    },
);

export const fetchPublishLiterature = createAsyncThunk(
    "technical_literature/publish",
    async (id: string, {rejectWithValue, dispatch}) => {
        try {
            return await technicalLiteratureAPI.publish(id);
        } catch (e) {
            const msg = handlerError(e);
            dispatch(setModalMessage(msg));
            return rejectWithValue(msg);
        }
    },
);

export const fetchUnpublishLiterature = createAsyncThunk(
    "technical_literature/unpublish",
    async (id: string, {rejectWithValue, dispatch}) => {
        try {
            return await technicalLiteratureAPI.unpublish(id);
        } catch (e) {
            const msg = handlerError(e);
            dispatch(setModalMessage(msg));
            return rejectWithValue(msg);
        }
    },
);
